import React, { useState, useContext, useEffect } from 'react';
import { firstBy } from 'thenby';
import usePrevious from '../Hooks/usePrevious';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import AppContext from '../Hooks/AppContext';
import { Link, useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ClearIcon from '@mui/icons-material/Clear';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

/**
 * Static core components
 */
import Highlighted from './Core/Highlighted';
import Navigation from './Core/Navigation';

/**
 * Dynamic dialog components
 */
import Entities from '../Views/Entities';
import Branch from './Branch';
import DefaultBranch from './DefaultBranch';
import DisableBranch from './DisableBranch';
import EnableBranch from './EnableBranch';
import BranchPrograms from './BranchPrograms';

const defaultFilter = {
  idBeneficiary: '',
  text: '',
};
const defaultDeps = {
  beneficiaries: [],
};
const defaultSelection = {
  entity: null,
  op: null,
  entities: [],
  anchorEl: null,
};

const Branches = props => {
  /**
   * Constants
   */

  /**
   * States
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const [filter, setFilter] = useState(defaultFilter);
  const [data, setData] = useState(null);
  const [deps, setDeps] = useState(defaultDeps);
  const [defs, setDefs] = useState(null);
  const [selection, setSelection] = useState(defaultSelection);

  /**
   * Hooks
   */
  const ac = useContext(AppContext);
  const params = useParams();
  const prev = usePrevious({params});

  /**
   * Effects
   */
  useEffect(() => {
    if (JSON.stringify(params) === JSON.stringify(prev?.params)) return;

    ac.ajax('getBranches', {
      idBeneficiary: params.idBeneficiary,
    })
      .then(response => {
        if (response.status.ok) {
          setFilter(getDefaultFilter());
          setData(response.result.data.branches);
          setDeps(response.result.dependencies);
          setDefs(response.result.defaultValues);

          setIsLoaded(true);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  /**
   * Getters
   */
  const getDefaultFilter = () => ({
    ...defaultFilter,
    idBeneficiary: params.idBeneficiary,
  });
  const getBeneficiaries = () => deps.beneficiaries.sort(firstBy('beneficiary'));

  /**
   * Handlers
   */
  const handleFilterChange = () => evt => {
    setFilter({
      ...filter,
      [evt.target.name]: evt.target.value,
    });
  };
  const handleFilterTextReset = () => () => {
    setFilter({
      ...filter,
      text: defaultFilter.text,
    });
  };
  const handleFilterReset = () => () => {
    setFilter(getDefaultFilter());
  };
  const handleSelectionChange = (value = null, showMenu = null) => (evt = null) => {
    if (evt !== null && 'stopPropagation' in evt) evt.stopPropagation();

    setSelection(prevState => ({
      ...prevState,
      ...value,
      anchorEl: showMenu === true ? evt.currentTarget : showMenu === false ? null : selection.anchorEl,
    }));
  };
  const handleReload = (response, newSelection = null) => {
    setData(response.result.data.branches);
    setDeps(response.result.dependencies);
    setDefs(response.result.defaultValues);

    handleSelectionChange(
      newSelection
        ? newSelection
        : response.result.lastId
          ? {
              ...defaultSelection,
              entity: response.result.data.branches.find(item => item.id === response.result.lastId),
              entities: [response.result.lastId],
            }
          : selection
    )();
  };

  /**
   * Subcomponents render
   */
  const renderFilter = () => (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <TextField select
          disabled={ac.isLoading}
          label='Beneficiar'
          placeholder='Beneficiar'
          name='idBeneficiary'
          value={filter.idBeneficiary}
        >
          {getBeneficiaries().map(item => (
            <MenuItem key={item.id} component={Link} to={`/Admin/Branches/${item.id}`} className='link' value={item.id}>
              <Typography variant='body1' fontWeight={item.isMyCompany ? 'bold' : 'initial'}>{item.beneficiary}</Typography>
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <TextField
          InputProps={{
            endAdornment: filter.text !== defaultFilter.text
              ? <InputAdornment position='end'>
                  <IconButton edge='end' size='small' color='error'
                    onClick={handleFilterTextReset()}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              : null,
          }}
          disabled={ac.isLoading || data?.length === 0}
          label='Filtrare rapida'
          placeholder='Filtrare rapida'
          name='text'
          value={filter.text}
          onChange={handleFilterChange()}
        />

        <Tooltip title='Reseteaza filtrele la valoarile initiale'>
          <div>
            <IconButton color='error'
              disabled={JSON.stringify(filter) === JSON.stringify(getDefaultFilter())}
              onClick={handleFilterReset()}
            >
              <FilterAltOffIcon />
            </IconButton>
          </div>
        </Tooltip>
      </Grid>
    </Grid>
  );

  /**
   * Renderer
   */
  if (!isLoaded) {
    return null;
  }

  return (
    <Container maxWidth='md' fixed>
      <Navigation
        paths={[
          {text: 'Acasa'          , path: '/'                   , icon: 'Home'      },
          {text: 'Administrare'   , path: '/Admin'              , icon: 'Settings'  },
          {text: 'Beneficiari'    , path: '/Admin/Beneficiaries', icon: 'Store'     },
          {text: 'Puncte de lucru', path: null                  , icon: 'Storefront'},
        ]}
      />

      {renderFilter()}

      <Entities defaultFilter={defaultFilter} filter={filter} data={data} deps={deps} selection={selection}
        sortBy={['county', 'branch']}
        groupBy='county'
        renderEntity={{
          avatar: null,
          icon: null,
          textPrimary: entity => (
            <Highlighted highlight={filter.text} variant='body2' color={entity.isEnabled ? 'textPrimary' : 'rgba(0, 0, 0, 0.26)'} sx={{flexGrow: 1, fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{entity.branch}</Highlighted>
          ),
          textSecondary: [
            entity => <Highlighted highlight={filter.text}>{entity.city}, {entity.address}, jud. {entity.county}</Highlighted>,
            entity => <Highlighted highlight={filter.text}>{entity.phone} / {entity.email}</Highlighted>,
          ],
          textIcons: [
            entity => ({text: 'Acest punct de lucru este definit ca implicit', icon: 'Home'         , color: 'primary' , when: entity.isDefault }),
            entity => ({text: 'Acest punct de lucru este activ'              , icon: 'CheckCircle'  , color: 'primary' , when: entity.isEnabled }),
            entity => ({text: 'Acest punct de lucru nu este activ'           , icon: 'Cancel'       , color: 'error'   , when: !entity.isEnabled}),
          ],
        }}
        contextualMenu={[
          entity => ({text: 'Modifica'        , icon: null, action: 'editEntity'                                                                   , when: true                                                                          }),
          entity => ({text: 'Seteaza implicit', icon: null, action: 'setDefaultBranch'                                                             , when: selection.entity.isEnabled && !selection.entity.isDefault                     }),
          entity => ({text: 'Dezactiveaza'    , icon: null, action: 'disableEntity'                                                                , when: !selection.entity.isDefault && selection.entity.isEnabled                     }),
          entity => ({text: 'Activeaza'       , icon: null, action: 'enableEntity'                                                                 , when: !selection.entity.isEnabled                                                   }),
          entity => ({text: 'Program de lucru', icon: null, action: 'branchProgram'                                                                , when: deps.beneficiaries.find(item => item.id === filter.idBeneficiary)?.isMyCompany}),
          entity => ({text: 'Imprimante'      , icon: null, action: `/Admin/Printers/${selection.entity.id}`                                       , when: deps.beneficiaries.find(item => item.id === filter.idBeneficiary)?.isMyCompany}),
          entity => ({text: 'Preturi servicii', icon: null, action: `/Admin/ServicePrices/MyCompany/${params.idBeneficiary}/${selection.entity.id}`, when: deps.beneficiaries.find(item => item.id === filter.idBeneficiary)?.isMyCompany}),
        ]}
        newEntity={{
          op: 'editEntity',
          entity: {
            ...defs.branch,
            idBeneficiary: params.idBeneficiary,
          },
        }}
        onChangeSelection={setSelection}
      />

      {/**
       * Add/edit entity
       */}
      {Boolean(selection.entity) && selection.op === 'editEntity' &&
        <Branch
          value={selection.entity}
          onCancel={handleSelectionChange({op: defaultSelection.op})}
          onConfirm={handleReload}
        />
      }

      {/**
       * Set default entity
       */}
      {Boolean(selection.entity) && selection.op === 'setDefaultBranch' &&
        <DefaultBranch
          value={selection.entity}
          onCancel={handleSelectionChange({op: defaultSelection.op})}
          onConfirm={handleReload}
        />
      }

      {/**
       * Set entity as disabled
       */}
      {Boolean(selection.entity) && selection.op === 'disableEntity' &&
        <DisableBranch
          value={selection.entity}
          onCancel={handleSelectionChange({op: defaultSelection.op})}
          onConfirm={handleReload}
        />
      }

      {/**
       * Set entity as enabled
       */}
      {Boolean(selection.entity) && selection.op === 'enableEntity' &&
        <EnableBranch
          value={selection.entity}
          onCancel={handleSelectionChange({op: defaultSelection.op})}
          onConfirm={handleReload}
        />
      }

      {/**
       * Manage branch program
       */}
      {Boolean(selection.entity) && selection.op === 'branchProgram' &&
        <BranchPrograms
          value={selection.entity}
          onCancel={handleSelectionChange({op: defaultSelection.op})}
          onConfirm={handleReload}
        />
      }
    </Container>
  );
};

export default Branches;